// Ayudas puras de cálculo para que el paso de "cálculo" del cotizador responda
// al instante mientras el usuario edita. La fuente de verdad sigue siendo el
// estimate del servidor (quoteEngine.js); estas fórmulas sólo la espejan.

import type { QuoteEstimate, QuoteLine, QuoteOverrides, TollItem } from "./types";

export function round2(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function sumLines(lines: Pick<QuoteLine, "total">[]): number {
  return round2(lines.reduce((s, l) => s + (Number(l.total) || 0), 0));
}

/** Margen sobre precio: precio = costo / (1 − m). */
export function priceFromMargin(cost: number, marginPct: number): number {
  const m = Math.min(Math.max(Number(marginPct) || 0, 0), 95);
  return round2((Number(cost) || 0) / (1 - m / 100));
}

/** Margen (sobre precio) que resulta de un precio dado. */
export function marginFromPrice(cost: number, price: number): number {
  const p = Number(price) || 0;
  if (p <= 0) return 0;
  return round2(((p - (Number(cost) || 0)) / p) * 100);
}

export function applyTax(price: number, ivaPct: number, retentionPct: number) {
  const p = Number(price) || 0;
  const iva_amount = round2((p * (Number(ivaPct) || 0)) / 100);
  const retention_amount = round2((p * (Number(retentionPct) || 0)) / 100);
  return { iva_amount, retention_amount, total_with_tax: round2(p + iva_amount - retention_amount) };
}

/** Multiplica casetas base (precio de auto) por el factor por ejes. */
export function applyTollFactor(items: TollItem[], factor: number): { items: TollItem[]; total: number } {
  const f = Number(factor) > 0 ? Number(factor) : 1;
  const out = items.map((it) => {
    const base = Number(it.base_price ?? it.price) || 0;
    return { ...it, base_price: round2(base), price: round2(base * f) };
  });
  return { items: out, total: round2(out.reduce((s, it) => s + it.price, 0)) };
}

export function sumTolls(items: Pick<TollItem, "price">[]): number {
  return round2(items.reduce((s, it) => s + (Number(it.price) || 0), 0));
}

export interface LocalRecomputeInput {
  estimate: QuoteEstimate;
  overrides: QuoteOverrides;
  /** Casetas capturadas manualmente (reemplazan la línea `tolls`). */
  manualTolls?: TollItem[] | null;
}

/**
 * Re-aplica overrides sobre un estimate del servidor sin volver a llamarlo.
 * Espeja a `quoteEngine.buildQuote`: líneas base → overrides/quitar/custom →
 * overhead sobre el nuevo subtotal → margen sobre precio → impuestos.
 */
export function recomputeEstimate({ estimate, overrides, manualTolls }: LocalRecomputeInput): QuoteEstimate {
  const removed = new Set(overrides.remove ?? []);
  const lineOverrides = overrides.lines ?? {};
  const base = estimate.lines.filter((l) => l.key !== "overhead" && !l.custom);

  const applied: QuoteLine[] = base
    .filter((l) => !removed.has(l.key))
    .map((l) => {
      let next: QuoteLine = { ...l, overridden: false };
      if (l.key === "tolls" && manualTolls) {
        const total = sumTolls(manualTolls);
        next = { ...next, qty: 1, unit_cost: total, total, overridden: true };
      }
      const ov = lineOverrides[l.key];
      if (ov) {
        if (ov.label) next.label = ov.label;
        if (ov.total != null && Number.isFinite(Number(ov.total))) {
          next.total = round2(Number(ov.total));
          next.qty = next.qty > 0 ? next.qty : 1;
          next.unit_cost = round2(next.total / next.qty);
        } else if (ov.unit_cost != null && Number.isFinite(Number(ov.unit_cost))) {
          next.unit_cost = round2(Number(ov.unit_cost));
          next.total = round2(next.qty * next.unit_cost);
        }
        next.overridden = true;
      }
      return next;
    });

  (overrides.custom_lines ?? []).forEach((cl, i) => {
    if (!cl?.label) return;
    const qty = cl.qty != null ? Number(cl.qty) : 1;
    const unit_cost = Number(cl.unit_cost) || 0;
    applied.push({
      key: `custom_${i + 1}`,
      label: cl.label,
      qty: round2(qty),
      unit: cl.unit || "viaje",
      unit_cost: round2(unit_cost),
      total: round2(qty * unit_cost),
      custom: true,
    });
  });

  const subtotal = sumLines(applied);
  if (!removed.has("overhead")) {
    const overheadBase = estimate.lines.find((l) => l.key === "overhead");
    let overhead: QuoteLine = {
      key: "overhead",
      label: overheadBase?.label ?? "Gastos indirectos",
      qty: 1,
      unit: "viaje",
      unit_cost: round2((subtotal * (estimate.overhead_pct || 0)) / 100),
      total: round2((subtotal * (estimate.overhead_pct || 0)) / 100),
      meta: { pct: estimate.overhead_pct },
    };
    const ov = lineOverrides.overhead;
    if (ov?.total != null) overhead = { ...overhead, total: round2(Number(ov.total)), unit_cost: round2(Number(ov.total)), overridden: true };
    applied.push(overhead);
  }

  const cost_total = sumLines(applied);
  let margin_pct = overrides.margin_pct != null ? Number(overrides.margin_pct) : estimate.margin_pct;
  margin_pct = Math.min(Math.max(margin_pct || 0, 0), 95);
  let price: number;
  if (overrides.price != null && Number(overrides.price) > 0) {
    price = round2(Number(overrides.price));
    margin_pct = marginFromPrice(cost_total, price);
  } else {
    price = priceFromMargin(cost_total, margin_pct);
  }
  const tax = applyTax(price, estimate.taxes.iva_pct, estimate.taxes.retention_pct);
  const tollsLine = applied.find((l) => l.key === "tolls");

  return {
    ...estimate,
    lines: applied,
    subtotal,
    cost_total,
    cost_per_km: estimate.distance_km > 0 ? round2(cost_total / estimate.distance_km) : 0,
    margin_pct: round2(margin_pct),
    price,
    taxes: { ...estimate.taxes, iva_amount: tax.iva_amount, retention_amount: tax.retention_amount },
    total_with_tax: tax.total_with_tax,
    tolls: manualTolls
      ? { ...estimate.tolls, source: "manual", axle_factor: 1, items: manualTolls, total: tollsLine?.total ?? sumTolls(manualTolls) }
      : estimate.tolls,
  };
}
